import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
// Import des nouveaux icônes pour les features
import { 
  ExternalLink, 
  Github, 
  Calendar, 
  Tag,
  Play,
  ChevronDown,
  ChevronUp,
  Shield,
  Mic,
  Sparkles,
  Clock,
  Smartphone
} from 'lucide-react';
import { projects as projectsData } from '../../data/projects';
import LegoCard from '../ui/LegoCard';
import ProjectImage from '../ui/ProjectImage';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Projects section component showcasing featured projects
 * Features Alfred cooking assistant and other projects
 */

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[8]};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing[6]};
  }
`;

const FeaturedProjectContent = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[6]};
  position: relative;
  z-index: 2;
  align-items: flex-start;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing[4]};
  }
`;

const ProjectCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[4]};
  padding-top: ${props => props.theme.spacing[2]};
`;

// ProjectCard supprimé car remplacé par LegoCard

// Suppression des composants obsolètes
// const ProjectCard = styled(motion.div)...
// const itemVariants = ...

// ProjectImage styled component supprimé - remplacé par le composant ProjectImage

const FeaturedImage = styled.div`
  flex: 1;
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  gap: 96px;
`;

// ProjectIcon supprimé - remplacé par ProjectImage

const ProjectContent = styled.div`
  padding: ${props => props.theme.spacing[6]};
  position: relative;
  z-index: 1;
`;

const FeaturedContent = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing[8]};
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 400px;
`;

const ProjectTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes['2xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const FeaturedTitle = styled.h2`
  font-size: ${props => props.theme.typography.sizes['4xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: white;
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const ProjectDescription = styled.p`
  font-size: ${props => props.theme.typography.sizes.base};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const FeaturedDescription = styled.div`
  color: rgba(255, 255, 255, 0.9);
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  margin-bottom: ${props => props.theme.spacing[6]};
  max-width: 600px;
`;

const IntroText = styled.p`
  font-size: ${props => props.theme.typography.sizes.lg};
  margin-bottom: ${props => props.theme.spacing[5]};
  font-weight: ${props => props.theme.typography.weights.medium};
  color: white;
`;

const FeaturesContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing[5]};
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[3]};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing[3]};
`;

const FeatureIcon = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h4`
  font-size: ${props => props.theme.typography.sizes.base};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: white;
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const FeatureText = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: white;
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
`;

const ObjectiveContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing[4]};
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
`;

const ObjectiveTitle = styled.h4`
  font-size: ${props => props.theme.typography.sizes.base};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: white;
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const ObjectiveText = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: rgba(255, 255, 255, 0.9);
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
`;

const ProjectMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[3]};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

const FeaturedMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const FeaturedMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  font-size: ${props => props.theme.typography.sizes.base};
  color: rgba(255, 255, 255, 0.8);
`;

// TechStack components moved above for better organization

const ProjectActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[3]};
`;

const FeaturedActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[4]};
`;

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-weight: ${props => props.theme.typography.weights.medium};
  text-decoration: none;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
`;

const PrimaryButton = styled(ActionButton)`
  background-color: ${props => props.theme.colors.primary[600]};
  color: white;

  &:hover {
    background-color: ${props => props.theme.colors.primary[700]};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

// SecondaryButton supprimé car non utilisé

const FeaturedPrimaryButton = styled(ActionButton)`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const FeaturedSecondaryButton = styled(ActionButton)`
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.5);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: white;
    transform: translateY(-2px);
  }
`;

const AppStoreButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[4]};
  justify-content: center;
`;
const AppStoreButton = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-weight: ${props => props.theme.typography.weights.medium};
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  cursor: not-allowed;
  opacity: 0.7;
  position: relative;
`;

const SoonBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: ${props => props.theme.colors.warning};
  color: white;
  font-size: ${props => props.theme.typography.sizes.xs};
  padding: 2px 6px;
  border-radius: ${props => props.theme.borderRadius.full};
  font-weight: ${props => props.theme.typography.weights.bold};
`;

const AppStoreIcon = styled.div`
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: #000;
`;

// Tags expansion system
const TechStackContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
`;

const TechTag = styled.span`
  background-color: ${props => props.theme.colors.primary[100]};
  color: ${props => props.theme.colors.primary[700]};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.medium};
`;

const FeaturedTechTag = styled.span`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.medium};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary[600]};
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.medium};
  cursor: pointer;
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  
  &:hover {
    background-color: ${props => props.theme.colors.primary[50]};
  }
`;

const FeaturedExpandButton = styled(ExpandButton)`
  color: rgba(255, 255, 255, 0.8);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.sizes.xs};
  font-weight: ${props => props.theme.typography.weights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: ${props => {
    const colors = {
      completed: props.theme.colors.success,
      'in-progress': props.theme.colors.warning,
      planned: props.theme.colors.info,
    };
    return colors[props.$status as keyof typeof colors] || props.theme.colors.neutral[400];
  }};
  color: white;
`;

// Component pour afficher la description d'Alfred de manière structurée
const AlfredDescription: React.FC = () => {
  const { t } = useLanguage();
  
  const features = [
    {
      icon: <Shield size={20} />,
      title: t('projects.alfred.features.waste.title'),
      text: t('projects.alfred.features.waste.text')
    },
    {
      icon: <Mic size={20} />,
      title: t('projects.alfred.features.stress.title'),
      text: t('projects.alfred.features.stress.text')
    },
    {
      icon: <Sparkles size={20} />,
      title: t('projects.alfred.features.discover.title'),
      text: t('projects.alfred.features.discover.text')
    },
    {
      icon: <Clock size={20} />,
      title: t('projects.alfred.features.time.title'),
      text: t('projects.alfred.features.time.text')
    },
    {
      icon: <Smartphone size={20} />,
      title: t('projects.alfred.features.connected.title'),
      text: t('projects.alfred.features.connected.text')
    }
  ];

  return (
    <FeaturedDescription>
      <IntroText>
        {t('projects.alfred.intro')}
      </IntroText>
      
      <FeaturesContainer>
        <FeaturesList>
          {features.map((feature, index) => (
            <FeatureItem key={index}>
              <FeatureIcon>
                {feature.icon}
              </FeatureIcon>
              <FeatureContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureText>{feature.text}</FeatureText>
              </FeatureContent>
            </FeatureItem>
          ))}
        </FeaturesList>
      </FeaturesContainer>

      <ObjectiveContainer>
        <ObjectiveTitle>{t('projects.alfred.objective.title')}</ObjectiveTitle>
        <ObjectiveText>
          {t('projects.alfred.objective.text')}
        </ObjectiveText>
      </ObjectiveContainer>
    </FeaturedDescription>
  );
};

// Component for expandable tech stack
const ExpandableTechStack: React.FC<{
  technologies: string[];
  isFeatured?: boolean;
  maxVisible?: number;
}> = ({ technologies, isFeatured = false, maxVisible = 4 }) => {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  const visibleTechnologies = isExpanded ? technologies : technologies.slice(0, maxVisible);
  const hasMore = technologies.length > maxVisible;
  
  const TagComponent = isFeatured ? FeaturedTechTag : TechTag;
  const ExpandButtonComponent = isFeatured ? FeaturedExpandButton : ExpandButton;
  
  return (
    <TechStackContainer>
      <TechStack>
        {visibleTechnologies.map((tech) => (
          <TagComponent key={tech}>{tech}</TagComponent>
        ))}
      </TechStack>
      {hasMore && (
        <ExpandButtonComponent onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? (
            <>
              <ChevronUp size={14} />
              {t('projects.tech.collapse')}
            </>
          ) : (
            <>
              <ChevronDown size={14} />
              {t('projects.tech.expand')} ({technologies.length - maxVisible} {t('projects.tech.others')})
            </>
          )}
        </ExpandButtonComponent>
      )}
    </TechStackContainer>
  );
};

const Projects: React.FC = () => {
  const { t } = useLanguage();
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const featuredProject = projectsData.find(p => p.featured);
  const otherProjects = projectsData.filter(p => !p.featured);

  const getProjectTitle = (project: any) => {
    if (project.id === 'portfolio') {
      return t('projects.portfolio.title');
    } else if (project.id === 'systra-module') {
      return t('projects.systra.title');
    } else if (project.id === 'alfred') {
      return t('projects.alfred.title');
    }
    return project.title;
  };

  const getProjectDescription = (project: any) => {
    if (project.id === 'portfolio') {
      return t('projects.portfolio.description');
    } else if (project.id === 'systra-module') {
      return t('projects.systra.description');
    } else if (project.id === 'alfred') {
      return t('projects.alfred.description');
    }
    return project.description;
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return t('projects.meta.status.completed');
      case 'in-progress':
        return t('projects.meta.status.in-progress');
      case 'planned':
        return t('projects.meta.status.planned');
      default:
        return status;
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
    >
      <ProjectsGrid>
        {/* Featured Project */}
        {featuredProject && (
          <LegoCard 
            intensity="strong" 
            color="primary"
            style={{ gridColumn: '1 / -1' }}
          >
            <FeaturedProjectContent>
              <FeaturedImage>
                <ProjectImage 
                  src={featuredProject.image} 
                  alt={featuredProject.title}
                  isMobile={featuredProject.id === 'alfred'}
                  size={featuredProject.id === 'alfred' ? 'featured' : 'normal'}
                  screens={featuredProject.id === 'alfred' ? [
                    '/images/projects/alfred/alfred_home.png',
                    '/images/projects/alfred/alfred_pantry.png',
                    '/images/projects/alfred/alfred_scanner.png',
                    '/images/projects/alfred/alfred_cooking-mode.png'
                  ] : undefined}
                />
                {featuredProject.id === 'alfred' && (
                  <AppStoreButtons>
                    <AppStoreButton>
                      <AppStoreIcon>🍎</AppStoreIcon>
                      {t('projects.action.app-store')}
                      <SoonBadge>{t('projects.badge.soon')}</SoonBadge>
                    </AppStoreButton>
                    <AppStoreButton>
                      <AppStoreIcon>📱</AppStoreIcon>
                      {t('projects.action.google-play')}
                      <SoonBadge>{t('projects.badge.soon')}</SoonBadge>
                    </AppStoreButton>
                  </AppStoreButtons>
                )}
              </FeaturedImage>
              <FeaturedContent>
                <FeaturedTitle>{getProjectTitle(featuredProject)}</FeaturedTitle>
                {featuredProject.id === 'alfred' ? (
                  <AlfredDescription />
                ) : (
                  <FeaturedDescription>{featuredProject.longDescription}</FeaturedDescription>
                )}
                
                <FeaturedMeta>
                  <FeaturedMetaItem>
                    <Calendar size={16} />
                    {t('projects.meta.started')} {new Date(featuredProject.startDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </FeaturedMetaItem>
                  <FeaturedMetaItem>
                    <Tag size={16} />
                    {featuredProject.category.toUpperCase()}
                  </FeaturedMetaItem>
                  <FeaturedMetaItem>
                    <StatusBadge $status={featuredProject.status}>
                      {getStatusText(featuredProject.status)}
                    </StatusBadge>
                  </FeaturedMetaItem>
                </FeaturedMeta>

                <ExpandableTechStack 
                  technologies={featuredProject.technologies}
                  isFeatured={true}
                  maxVisible={6}
                />

                <FeaturedActions>
                  {featuredProject.id === 'alfred' ? (
                    // Pas de boutons ici pour Alfred, ils sont sous le téléphone
                    null
                  ) : (
                    <>
                      {featuredProject.liveUrl && (
                        <FeaturedPrimaryButton
                          href={featuredProject.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Play size={16} />
                          {t('projects.action.live-demo')}
                        </FeaturedPrimaryButton>
                      )}
                      {featuredProject.githubUrl && (
                        <FeaturedSecondaryButton
                          href={featuredProject.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Github size={16} />
                          {t('projects.action.view-code')}
                        </FeaturedSecondaryButton>
                      )}
                    </>
                  )}
                </FeaturedActions>
              </FeaturedContent>
            </FeaturedProjectContent>
          </LegoCard>
        )}

        {/* Other Projects */}
        {otherProjects.map((project, index) => (
          <LegoCard
            key={project.id}
            intensity={index % 2 === 0 ? 'medium' : 'light'}
            color={index % 3 === 0 ? 'secondary' : 'neutral'}
          >
            <ProjectCardContent>
              <ProjectImage 
                src={project.image} 
                alt={project.title}
                isMobile={project.id === 'alfred'}
                screens={project.id === 'alfred' ? [
                  '/images/projects/alfred/alfred_home.png',
                  '/images/projects/alfred/alfred_pantry.png',
                  '/images/projects/alfred/alfred_scanner.png',
                  '/images/projects/alfred/alfred_cooking-mode.png'
                ] : undefined}
              />
              <ProjectContent>
                <ProjectTitle>{getProjectTitle(project)}</ProjectTitle>
                <ProjectDescription>{getProjectDescription(project)}</ProjectDescription>
                
                <ProjectMeta>
                  <MetaItem>
                    <Calendar size={14} />
                    {new Date(project.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </MetaItem>
                  <MetaItem>
                    <Tag size={14} />
                    {project.category}
                  </MetaItem>
                  <MetaItem>
                    <StatusBadge $status={project.status}>
                      {getStatusText(project.status)}
                    </StatusBadge>
                  </MetaItem>
                </ProjectMeta>

                <ExpandableTechStack 
                  technologies={project.technologies}
                  isFeatured={false}
                  maxVisible={4}
                />

                <ProjectActions>
                  {project.id !== 'portfolio' && project.liveUrl && (
                    <PrimaryButton
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink size={14} />
                      {t('projects.action.live-demo')}
                    </PrimaryButton>
                  )}
                </ProjectActions>
              </ProjectContent>
            </ProjectCardContent>
          </LegoCard>
        ))}
      </ProjectsGrid>
    </motion.div>
  );
};

export default Projects;
